import { ComponentType, MouseEvent, useState } from 'react';
import dynamic from 'next/dynamic';
import * as S from './ParallaxGapBanner.styled';
import { useFirstInteraction } from '../../../hooks/useFirstInteraction';
import useScreenSize from '../../../hooks/useScreenSize';
import {
  PARALLAX_GAP_BANNER_AR_CN,
  PARALLAX_GAP_BANNER_BN,
  PARALLAX_GAP_BANNER_BN_MOBILE,
  PARALLAX_GAP_BANNER_CONTAINER_ID,
  PARALLAX_GAP_BANNER_CONTAINER_ID_MOBILE,
  PARALLAX_GAP_BANNER_SID,
  PARALLAX_GAP_HIDE_COOKIE,
  PARALLAX_GAP_HIDE_MAX_AGE_SECONDS,
  PARALLAX_GAP_STICKY_CONTENT_GAP_PX,
  PARALLAX_GAP_STICKY_VISIBLE_DESKTOP_PX,
  PARALLAX_GAP_STICKY_VISIBLE_MOBILE_PX
} from './constants';
import { BannerCreative, getSessionHidden, setSessionHidden } from './helpers';
import { BodyPortal } from './BodyPortal';
import { useParallaxCreative } from './useParallaxCreative';
import {
  ParallaxStickyPin,
  useParallaxStickyPin
} from './useParallaxStickyPin';

const Banner = dynamic<Record<string, unknown>>(
  () =>
    import('../../Banner').then(
      mod => mod.default as unknown as ComponentType<Record<string, unknown>>
    ),
  { ssr: false }
);

type ParallaxGapBannerProps = {
  isSearchPage: boolean;
};

const ParallaxGapAdriverBanner = ({
  bn,
  containerId
}: {
  bn: string;
  containerId: string;
}) => (
  <Banner
    key={containerId}
    tagName="div"
    containerId={containerId}
    sid={PARALLAX_GAP_BANNER_SID}
    bn={bn}
    arCn={PARALLAX_GAP_BANNER_AR_CN}
  />
);

const CloseBannerButton = ({
  onClose,
  isPinned
}: {
  onClose: () => void;
  isPinned?: boolean;
}) => (
  <S.CloseButton
    type="button"
    aria-label="Закрыть"
    $isPinned={isPinned}
    onClick={(event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      onClose();
    }}
  />
);

const PinnedCover = ({ pin }: { pin: ParallaxStickyPin }) => (
  <S.ContentCover
    $top={pin.top}
    $left={pin.left}
    $width={pin.width}
    $height={pin.height + PARALLAX_GAP_STICKY_CONTENT_GAP_PX}
  />
);

const MobileCreative = ({
  creative,
  pinCrop
}: {
  creative: BannerCreative;
  pinCrop?: boolean;
}) => (
  <S.ViewportMaskCreative $pinCrop={pinCrop}>
    <S.DesktopPromoLink
      href={creative.href}
      target="_blank"
      rel="noopener noreferrer"
    >
      <img src={creative.imgSrc} alt="" />
    </S.DesktopPromoLink>
  </S.ViewportMaskCreative>
);

const DesktopCreative = ({
  creative,
  onClose,
  pinCrop,
  isPinned
}: {
  creative: BannerCreative;
  onClose: () => void;
  pinCrop?: boolean;
  isPinned?: boolean;
}) => (
  <>
    <S.DesktopPromoLink
      href={creative.href}
      target="_blank"
      rel="noopener noreferrer"
    >
      <S.DesktopFixedBackground $image={creative.imgSrc} $pinCrop={pinCrop} />
    </S.DesktopPromoLink>
    <CloseBannerButton isPinned={isPinned} onClose={onClose} />
  </>
);

export function ParallaxGapBanner({ isSearchPage }: ParallaxGapBannerProps) {
  const { isSmall, isLarge } = useScreenSize();
  const hasUserInteracted = useFirstInteraction();
  const [isBannerVisible, setIsBannerVisible] = useState(() => {
    if (typeof window === 'undefined') return true;
    return !getSessionHidden(PARALLAX_GAP_HIDE_COOKIE);
  });

  const isDesktop = isLarge;
  const bn = isSmall ? PARALLAX_GAP_BANNER_BN_MOBILE : PARALLAX_GAP_BANNER_BN;
  const containerId = isSmall
    ? PARALLAX_GAP_BANNER_CONTAINER_ID_MOBILE
    : PARALLAX_GAP_BANNER_CONTAINER_ID;
  const enabled = isSearchPage && isBannerVisible && (isSmall || isLarge);
  const shouldRunBanner = enabled && hasUserInteracted;
  const stickyVisible = isDesktop
    ? PARALLAX_GAP_STICKY_VISIBLE_DESKTOP_PX
    : PARALLAX_GAP_STICKY_VISIBLE_MOBILE_PX;

  const [slotEl, setSlotEl] = useState<HTMLDivElement | null>(null);
  const creative = useParallaxCreative(shouldRunBanner, containerId);
  const pin = useParallaxStickyPin(slotEl, shouldRunBanner, stickyVisible);

  const closeBanner = () => {
    setSessionHidden(
      PARALLAX_GAP_HIDE_COOKIE,
      PARALLAX_GAP_HIDE_MAX_AGE_SECONDS
    );
    setIsBannerVisible(false);
  };

  if (!shouldRunBanner) return null;

  if (!creative) {
    return (
      <S.HiddenBannerLoader>
        <ParallaxGapAdriverBanner bn={bn} containerId={containerId} />
      </S.HiddenBannerLoader>
    );
  }

  return (
    <>
      <S.PromoGapSlot ref={setSlotEl} $isHidden={pin.isPinned}>
        {isDesktop && (
          <DesktopCreative creative={creative} onClose={closeBanner} />
        )}
        {!isDesktop && (
          <>
            {!pin.isPinned && (
              <S.MobileSlotCreative>
                <img src={creative.imgSrc} alt="" />
              </S.MobileSlotCreative>
            )}
            <S.DesktopPromoLink
              href={creative.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Реклама"
            />
          </>
        )}
      </S.PromoGapSlot>

      {isDesktop && pin.isPinned && (
        <BodyPortal>
          <PinnedCover pin={pin} />
          <S.StickyStrip
            $top={pin.top}
            $left={pin.left}
            $width={pin.width}
            $height={pin.height}
          >
            <DesktopCreative
              creative={creative}
              onClose={closeBanner}
              pinCrop
              isPinned
            />
          </S.StickyStrip>
        </BodyPortal>
      )}

      {!isDesktop && pin.isPinned && (
        <BodyPortal>
          <PinnedCover pin={pin} />
          <S.StickyStrip
            $top={pin.top}
            $left={pin.left}
            $width={pin.width}
            $height={pin.height}
          >
            <MobileCreative creative={creative} pinCrop />
            <CloseBannerButton isPinned onClose={closeBanner} />
          </S.StickyStrip>
        </BodyPortal>
      )}
    </>
  );
}
